import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  Paper,
  useTheme,
} from "@mui/material";
import {
  ContentCopy as SimilarIcon,
  MergeType as MergeIcon,
} from "@mui/icons-material";
import { tokens } from "../../../theme";
import type { SimilarContact } from "../../../types/contact";

interface SimilarContactsCardProps {
  similarContacts: SimilarContact[];
  loading: boolean;
  onContactClick: (contactId: number) => void;
  onMergeClick: (secondaryContactId: number) => void;
  canMerge: boolean;
}

const getSimilarityColor = (
  score: number,
): "error" | "warning" | "success" => {
  if (score >= 70) return "success";
  if (score >= 50) return "warning";
  return "error";
};

const getDisplayName = (contact: SimilarContact): string => {
  if (contact.contact_type === "company") return contact.name || "-";
  return [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "-";
};

export const SimilarContactsCard: React.FC<SimilarContactsCardProps> = ({
  similarContacts,
  loading,
  onContactClick,
  onMergeClick,
  canMerge,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Paper sx={{ p: 3, boxShadow: 0 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <SimilarIcon />
        <Typography variant="h6" fontWeight="medium">
          Similar Contacts
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : similarContacts.length === 0 ? (
        <Typography
          variant="body1"
          sx={{ fontStyle: "italic", textAlign: "center", py: 4 }}
        >
          No Similar Contacts Found
        </Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.primary[800] }}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell align="center">Similarity</TableCell>
                <TableCell align="center">Completeness</TableCell>
                {canMerge && <TableCell align="center">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {similarContacts.map((contact, index) => (
                <TableRow
                  key={contact.id}
                  sx={{
                    cursor: "pointer",
                    backgroundColor:
                      index % 2 === 0 ? "white" : colors.primary[800],
                    "&:hover": { backgroundColor: colors.grey[500] },
                  }}
                >
                  <TableCell onClick={() => onContactClick(contact.id)}>
                    <Typography variant="body2" fontWeight="medium">
                      {getDisplayName(contact)}
                    </Typography>
                  </TableCell>
                  <TableCell onClick={() => onContactClick(contact.id)}>
                    {contact.email || "-"}
                  </TableCell>
                  <TableCell onClick={() => onContactClick(contact.id)}>
                    {contact.phone && contact.phone !== "N/A"
                      ? contact.phone
                      : "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => onContactClick(contact.id)}
                  >
                    <Chip
                      label={`${contact.similarity_score}%`}
                      color={getSimilarityColor(contact.similarity_score)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => onContactClick(contact.id)}
                  >
                    <Chip
                      label={`${contact.completeness_score}%`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  {canMerge && (
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<MergeIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMergeClick(contact.id);
                        }}
                      >
                        Merge
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default SimilarContactsCard;
